/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */

import type { ChangeEvent, KeyboardEvent, ReactNode, HTMLProps, ReactElement } from 'react';
import { forwardRef, useEffect } from 'react';

import invoke from 'lodash/invoke';

import { KeyboardKeyEnum } from '@modules/app/appConstants';
import useClientRect from '@modules/app/hooks/useClientRect';
import { fonts } from '@theme/index';

import {
    InputContainerStyled,
    InputHintStyled,
    InputIconLeftStyled,
    InputIconRightStyled,
    InputWithIconStyled,
    LabelHintStyled,
    LabelMarkStyled,
    LabelStyled,
} from './styled/InputStyled';

export enum EInputCursor {
    auto = 'auto',
    pointer = 'pointer',
}

export enum EInputType {
    normal = 'normal',
    small = 'small',
}

export enum EInputKind {
    error = 'error',
    filled = 'filled',
    noborder = 'noborder',
    normal = 'normal',
    verify = 'verify',
}

/**
 * @description Define props for input component.
 * @interface IInputProps
 *
 * @property {boolean} autofocus - input autofocus
 * @property {string} inputtype native input type, ex. 'password', 'email'
 * @property {string} className Add className for main input container.
 * @property {EInputCursor} cursor Set cursor style to field and icons.
 * @property {boolean} disabled Is input field disabled.
 * @property {EInputKind} kind Props for change style according to theme settings.
 * @property {string} label Set label under input field.
 * @property {string} lefticon Define path to left icon.
 * @callback lefticonhandler Define handler onClick for left icon.
 * @callback onChange Define handler onChange for input.
 * @property {string} placeholder Define placeholder for input field.
 * @property {boolean} required Is input field required.
 * @property {string} rightIcon Define path to right icon.
 * @callback righticonhandler Define handler onClick for left icon.
 * @property {EInputType} type Define style type for input.
 * @property {string | number} value Define value for input filed.
 */
export interface IInputProps {
    autoFocus?: boolean;
    autoSelect?: boolean;
    children?: ReactNode;
    className?: string;
    cursor?: EInputCursor;
    dataName?: string;
    disabled?: boolean;
    inputtype?: string;
    kind?: EInputKind;
    label?: string;
    labelHint?: ReactElement;
    lefticon?: string;
    lefticonhandler?: () => void;
    hintMessage?: string;
    name?: string;
    onBlur?: {
        (e: FocusEvent): void;
        <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
    };
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onClick?: () => void;
    onEnter?: () => void;
    placeholder?: string;
    required?: boolean;
    righticon?: string;
    righticonhandler?: () => void;
    type?: EInputType;
    value?: string | number;
}

/**
 * @description Render input field with two optional icons and label with mark.
 * @extends IInputProps
 * @extends  React.HTMLProps<HTMLInputElement>
 */
const Input = forwardRef<HTMLDivElement, IInputProps & HTMLProps<HTMLInputElement>>(function Input(props, ref) {
    const [inputNode, inputRef] = useClientRect<HTMLInputElement>();

    const { autoFocus } = props;

    useEffect(() => {
        if (inputNode && autoFocus) {
            inputNode.focus();
        }
    }, [autoFocus, inputNode]);

    useEffect(() => {
        if (inputNode && props.autoSelect) {
            inputNode.select();
        }
    }, [inputNode, props.autoSelect]);

    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (props.onChange) {
            props.onChange(event);
        }
    };
    const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyboardKeyEnum.ENTER) {
            invoke(props, 'onEnter');
        }
    };
    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        autoSelect,
        children,
        className,
        cursor,
        hintMessage,
        kind,
        label,
        labelHint,
        lefticon,
        lefticonhandler,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onEnter,
        righticon,
        righticonhandler,
        type,
        dataName,
        ...inputFieldProps
    } = props;

    // Style settings for different input types
    const isSmall = type === 'small';
    const paddingLeft = lefticon ? (isSmall ? 32 : 56) : isSmall ? 12 : 20;
    const paddingRight = righticon ? (isSmall ? 32 : 56) : isSmall ? 12 : 20;
    const inputHeight = isSmall ? 32 : 48;
    const iconSize = isSmall ? 20 : 32;
    const positionMargin = isSmall ? 12 : 16;
    const font = isSmall ? fonts.textSmall : fonts.textMain;
    const labelFont = isSmall ? fonts.textSmallBold : fonts.textMainBold;
    return (
        <InputContainerStyled
            className={['input__container', className].join(' ')}
            cursor={cursor}
            data-name={props.dataName}
            font={font}
            inputHeight={inputHeight}
            kind={kind}
            leftIcon={lefticon}
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            ref={ref}
            rightIcon={righticon}
        >
            {label && (
                <LabelStyled className={'input__label'} kind={kind ? kind : EInputKind.normal} labelFont={labelFont}>
                    {label}
                    {inputFieldProps.required && <LabelMarkStyled className={'input__label-mark'}>*</LabelMarkStyled>}
                </LabelStyled>
            )}
            {labelHint && <LabelHintStyled>{labelHint}</LabelHintStyled>}
            <InputWithIconStyled>
                {lefticon && (
                    <InputIconLeftStyled
                        className={'input__input-icon'}
                        iconSize={iconSize}
                        positionMargin={positionMargin}
                        src={lefticon}
                        onClick={lefticonhandler}
                    />
                )}
                {righticon && (
                    <InputIconRightStyled
                        className={'input__input-icon'}
                        iconSize={iconSize}
                        positionMargin={positionMargin}
                        src={righticon}
                        onClick={righticonhandler}
                    />
                )}
                {children || (
                    <input
                        {...inputFieldProps}
                        autoComplete="off"
                        className={`input__input-block ${className} ${props.value ? 'input_filled' : ''}`}
                        data-name={dataName}
                        ref={inputRef}
                        type={props.inputtype}
                        onChange={onChangeHandler}
                        onKeyDown={onKeyDownHandler}
                    />
                )}
                {hintMessage && <InputHintStyled kind={props.kind}>{hintMessage}</InputHintStyled>}
            </InputWithIconStyled>
        </InputContainerStyled>
    );
});

Input.defaultProps = {
    cursor: EInputCursor.auto,
    hintMessage: '',
    disabled: false,
    kind: EInputKind.normal,
    label: '',
    lefticonhandler: () => {},
    onChange: () => {},
    onClick: () => {},
    placeholder: '',
    required: false,
    type: EInputType.normal,
};

export default Input;
